import { AuthProvider as OidcAuthProvider, useAuth } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { CircularProgress, Stack, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

const IDLE_WARN_MS   = 25 * 60 * 1000;  // 25 min — show warning dialog
const IDLE_LOGOUT_MS = 30 * 60 * 1000;  // 30 min — force re-login
const IDLE_CHECK_MS  = 30 * 1000;       // check every 30 s

const oidcConfig = {
  authority: import.meta.env.VITE_OIDC_AUTHORITY || 'https://sso.mercedes-benz.com',
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID || '',
  redirect_uri: `${window.location.origin}/callback`,
  post_logout_redirect_uri: window.location.origin,
  scope: 'openid profile email authorization_group entitlement_group',
  response_type: 'code',
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 300,
  userStore: new WebStorageStateStore({ store: sessionStorage }),
  onSigninCallback: () => {
    const returnUrl = sessionStorage.getItem('sentinel_return_url');
    sessionStorage.removeItem('sentinel_return_url');
    window.history.replaceState({}, document.title, returnUrl || '/');
  },
};

/**
 * Inner component that handles auth state rendering.
 * Shows loading spinner during auth, login button if not authenticated.
 */
function AuthGate({ children }: { children: ReactNode }) {
  const auth = useAuth();

  // Once the user has successfully authenticated once, we know we're past the
  // initial load — subsequent auth.isLoading states are background token renewals
  // and must NOT block the entire UI with the full-screen spinner.
  const hasAuthed = useRef(false);
  if (auth.isAuthenticated) hasAuthed.current = true;

  // --- Idle session management ---
  const lastActivityRef  = useRef<number>(Date.now());
  const [showIdleWarn, setShowIdleWarn]   = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(0);

  // Track any user activity to reset the idle clock
  useEffect(() => {
    const resetActivity = () => {
      lastActivityRef.current = Date.now();
      setShowIdleWarn(false);
    };
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const;
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, resetActivity));
  }, []);

  // Periodic idle check: warn at IDLE_WARN_MS, force re-login at IDLE_LOGOUT_MS
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const interval = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= IDLE_LOGOUT_MS) {
        clearInterval(interval);
        setShowIdleWarn(false);
        auth.removeUser().then(() => auth.signinRedirect());
      } else if (idleMs >= IDLE_WARN_MS) {
        setIdleCountdown(Math.ceil((IDLE_LOGOUT_MS - idleMs) / 1000));
        setShowIdleWarn(true);
      }
    }, IDLE_CHECK_MS);
    return () => clearInterval(interval);
  }, [auth.isAuthenticated, auth]);

  // 1-second countdown ticker while the warning dialog is visible
  useEffect(() => {
    if (!showIdleWarn) return;
    const ticker = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      const remaining = IDLE_LOGOUT_MS - idleMs;
      if (remaining <= 0) { clearInterval(ticker); return; }
      setIdleCountdown(Math.ceil(remaining / 1000));
    }, 1000);
    return () => clearInterval(ticker);
  }, [showIdleWarn]);

  // "Stay Logged In" — reset idle timer and silently renew the token
  const handleStayLoggedIn = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowIdleWarn(false);
    auth.signinSilent().catch(() => auth.signinRedirect());
  }, [auth]);

  const handleLogin = useCallback(() => {
    auth.signinRedirect();
  }, [auth]);

  // Auto-redirect to SSO login when not authenticated (must be before early returns)
  // Skip on /callback page to avoid racing with OIDC callback processing
  useEffect(() => {
    const isCallback = window.location.pathname === '/callback' || window.location.search.includes('code=');
    if (!auth.isAuthenticated && !auth.isLoading && !auth.error && !isCallback) {
      auth.signinRedirect();
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.error, auth]);

  // Listen for sentinel:token-expired events fired by the network interceptor
  // when it detects an expired token. Attempt silent renewal and notify the
  // interceptor of the result so it can retry the original request.
  useEffect(() => {
    const handleTokenExpired = async () => {
      try {
        await auth.signinSilent();
        window.dispatchEvent(new CustomEvent('sentinel:token-renewed'));
      } catch {
        // Signal failure so the interceptor can fall back to a hard redirect.
        window.dispatchEvent(new CustomEvent('sentinel:token-renewed'));
      }
    };
    window.addEventListener('sentinel:token-expired', handleTokenExpired);
    return () => window.removeEventListener('sentinel:token-expired', handleTokenExpired);
  }, [auth]);

  // Recover from screen lock / idle: when the tab becomes visible again, check
  // if the token is still valid and trigger a silent renew if needed.
  // NOTE: auth.signinSilent() sets isLoading=true momentarily, but with
  // hasAuthed.current=true the full-screen spinner is suppressed below.
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden || !auth.isAuthenticated) return;
      const user = auth.user;
      if (!user?.expires_at) return;
      const nowSec = Math.floor(Date.now() / 1000);
      if (user.expires_at - nowSec < 60) {
        try {
          await auth.signinSilent();
        } catch {
          auth.signinRedirect();
          return;
        }
      }
      window.dispatchEvent(new CustomEvent('sentinel:visibility-restored'));
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [auth]);

  // Auto-redirect on silent renew failures (e.g. "IFrame timed out")
  // instead of showing a dead-end error screen.
  useEffect(() => {
    if (auth.error) {
      const msg = auth.error.message || '';
      if (msg.includes('IFrame') || msg.includes('timed out') || msg.includes('Silent')) {
        auth.removeUser().then(() => auth.signinRedirect()).catch(() => auth.signinRedirect());
      }
    }
  }, [auth, auth.error]);

  // Show full-screen spinner ONLY during the very first auth handshake.
  // After hasAuthed.current is set, background token renewals run silently.
  if (auth.isLoading && !hasAuthed.current) {
    return (
      <Stack width="100%" height="100vh" alignItems="center" justifyContent="center" gap={1}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Authenticating...
        </Typography>
      </Stack>
    );
  }

  if (auth.error) {
    const msg = auth.error.message || '';
    // Silent renew errors are handled by the useEffect above — show a spinner while redirecting
    if (msg.includes('IFrame') || msg.includes('timed out') || msg.includes('Silent')) {
      return (
        <Stack width="100%" height="100vh" alignItems="center" justifyContent="center" gap={1}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Session expired, redirecting to login...
          </Typography>
        </Stack>
      );
    }
    return (
      <Stack width="100%" height="100vh" alignItems="center" justifyContent="center" gap={2}>
        <Typography color="error">Authentication error: {auth.error.message}</Typography>
        <Button variant="contained" onClick={handleLogin}>
          Try Again
        </Button>
      </Stack>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Stack width="100%" height="100vh" alignItems="center" justifyContent="center" gap={1}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Redirecting to login...
        </Typography>
      </Stack>
    );
  }

  const warnMinutes = Math.floor(idleCountdown / 60);
  const warnSeconds = idleCountdown % 60;

  return (
    <>
      {children}
      <Dialog open={showIdleWarn} disableEscapeKeyDown>
        <DialogTitle>Session Expiring Soon</DialogTitle>
        <DialogContent>
          <Typography>
            You have been inactive for a while. Your session will expire in{' '}
            <strong>{warnMinutes}:{String(warnSeconds).padStart(2, '0')}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleStayLoggedIn}>
            Stay Logged In
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Check whether a mock OIDC token exists in sessionStorage.
 * Used to bypass the real OIDC gate during local development.
 */
function hasLocalMockToken(): boolean {
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith('oidc.user:')) {
      try {
        const raw = JSON.parse(sessionStorage.getItem(key) || '');
        if (raw?.access_token) return true;
      } catch { /* ignore */ }
    }
  }
  return false;
}

/**
 * Top-level auth provider. Wraps the app with OIDC context.
 * In local-dev mode (`VITE_OIDC_CLIENT_ID=sentinel-local-test`) with a mock
 * sessionStorage token, the OIDC gate is bypassed entirely.
 */
export default function AuthProvider({ children }: { children: ReactNode }) {
  const isLocalDev = oidcConfig.client_id === 'sentinel-local-test';
  if (isLocalDev && hasLocalMockToken()) {
    return <>{children}</>;
  }
  return (
    <OidcAuthProvider {...oidcConfig}>
      <AuthGate>{children}</AuthGate>
    </OidcAuthProvider>
  );
}

export { useAuth };
